/**
 * @file        src/modals/VoucherModal.jsx
 * @description PDF-style printable booking voucher. Wraps the rendered doc
 *              in `#voucherDoc` so the `@media print` rule in the design's
 *              global stylesheet can target it. Ported from `#voucherModal`
 *              (~line 2069) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-12 Claude — Modal wrappers marked .print-unclip so a printed voucher is not clipped to the 90vh scroll box (Safari/Mac printed one page).
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-08-04 Claude — CR B3: FlightVoucherDoc's price rows now render
 *                          through the shared <PriceSummary variant="voucher">
 *                          (components/PriceSummary.jsx), replacing its own
 *                          `PriceRow` calls; baggage sum/count moved to
 *                          lib/fareBreakdown.js. HotelVoucherDoc (a different
 *                          product, out of scope for this flight-breakdown CR)
 *                          keeps using the local `PriceRow` unchanged.
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-06-24 Claude — Voucher wordmark now uses the home-page neon colour
 *                         (text-neon) instead of dark navy.
 *   - 2026-06-22 Claude — Initial port. Reads booking-store state to render
 *                         either the hotel voucher or the flight voucher
 *                         (mirrors `hotelVoucherDoc()` / `flightVoucherDoc()`
 *                         from the source).
 */
import { useEffect } from 'react';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';
import NeonWordmark from '../components/NeonWordmark.jsx';
import { LOGO_SIZE } from '../components/BrandLogo.jsx';
import { AIRPORTS } from '../data/airports.js';
import PriceSummary from '../components/PriceSummary.jsx';
import { baggageTotal, addonCount } from '../lib/fareBreakdown.js';

// "DEL T1" → "DEL"; airport code → city name; ISO → "Mon, 15 Jun".
const apCode = (s) => String(s || '').split(' ')[0];
const cityOf = (s) => AIRPORTS[apCode(s)]?.[0] || apCode(s);
function vDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function VoucherHeader() {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
      <NeonWordmark className={LOGO_SIZE + ' text-neon'} />
      <div className="text-right text-[11px] text-gray-500 leading-relaxed">
        Mobile : +91 - 9371502300 | 9372502300<br />
        Email : info@beyonddestination.in<br />
        Beyond Destination, Shop No 04, Gurudarshan Complex, Central Ave, Chapru Nagar, Sq, Nagpur, Maharashtra 440008
      </div>
    </div>
  );
}

function PriceRow({ k, v, bold }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'font-bold text-[#0A0F1E] border-t border-gray-200 mt-1 pt-2 text-base' : 'text-gray-600'}`}>
      <span>{k}</span>
      <span className={bold ? '' : 'text-[#0A0F1E]'}>{v}</span>
    </div>
  );
}

function FlightVoucherDoc({ legs, flight, fare, selectedFare, seat, seatPrice, baggageSel, paxName, guest, userEmail }) {
  // Every chosen leg (round trip = 2, multi-city = N); one-way falls back to the
  // single flight. Each leg renders its own itinerary block.
  const list = (legs && legs.length ? legs : [flight]).filter(Boolean);
  const multi = list.length > 1;
  const tripLabel = !multi ? 'One-way' : (selectedFare?.name === 'Multi-city' ? 'Multi-city' : 'Round trip');
  const legLabel = (i) =>
    !multi ? '' : tripLabel === 'Round trip' ? (['Onward', 'Return'][i] || `Flight ${i + 1}`) : `Flight ${i + 1}`;

  const seatLabel = (!multi && seat) ? seat : 'Assigned at check-in';
  const pax = paxName || guest?.fullName || 'Passenger 1';
  // Itinerary total = sum of every leg (fare.price already carries this for live
  // bookings); add-ons are per booking.
  const base = list.reduce((s, l) => s + (Number(l.total) || Number(l.price) || 0), 0) || Number(fare?.price) || 0;
  const sp = seatPrice || 0;
  const bg = baggageTotal(baggageSel);
  const bagCount = addonCount(baggageSel);
  const grand = base + sp + bg;
  const ref = 'NH' + String(grand).slice(0, 6);

  return (
    <>
      <VoucherHeader />
      <div className="mt-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-[#10b981] font-bold">Confirmed.</p>
          <p className="text-xs text-gray-500">Booking ID: <span className="font-bold text-[#0A0F1E]">{ref}</span></p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide text-[#2563EB] bg-blue-50 border border-blue-100 rounded px-2 py-1">{tripLabel}{multi ? ` · ${list.length} flights` : ''}</span>
      </div>

      {list.map((f, i) => (
        <div key={i} className={i > 0 ? 'mt-5 pt-4 border-t border-gray-200' : 'mt-4'}>
          <h3 className="font-bold text-base">
            {multi && <span className="text-[#2563EB]">{legLabel(i)} · </span>}
            {cityOf(f.from)} ({apCode(f.from)}) → {cityOf(f.to)} ({apCode(f.to)})
          </h3>
          <p className="text-xs text-gray-500">{f.airline} · {f.code} · {f.cabinName || f.cabinClass || 'Economy'} class</p>
          <div className="grid grid-cols-4 gap-px mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-200 text-center">
            <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Departure</p><p className="font-semibold">{vDate(f.depISO) || '—'}</p><p className="font-semibold">{f.dep}</p></div>
            <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Arrival</p><p className="font-semibold">{vDate(f.arrISO) || '—'}</p><p className="font-semibold">{f.arr}</p></div>
            <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Duration</p><p className="font-semibold">{f.dur}</p><p className="font-semibold">{f.stops}</p></div>
            <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Seat</p><p className="font-semibold">{multi ? 'Auto' : seatLabel}</p><p className="font-semibold">Economy</p></div>
          </div>
        </div>
      ))}

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div><p className="text-[11px] text-gray-500">Passenger</p><p className="font-semibold">{pax}</p></div>
        <div><p className="text-[11px] text-gray-500">Contact</p><p className="font-semibold">{guest?.email || userEmail || '—'}</p><p className="font-semibold">{guest?.phone || '—'}</p><p className="text-[11px] text-gray-500 mt-2">Baggage</p><p className="font-semibold">{list[0]?.baggage?.checkIn || '—'}{bagCount ? ` + ${bagCount} extra` : ''}</p></div>
      </div>
      <h4 className="font-bold mt-5 text-[#0A0F1E]">Price Summary</h4>
      <PriceSummary
        variant="voucher"
        rows={[
          { key: 'base', label: `Base Fare${multi ? ` (${list.length} flights)` : ' (1 adult)'}`, value: inr(base) },
          { key: 'seat', label: `Seat (${seat || '-'})`, value: inr(sp), show: sp > 0 },
          { key: 'baggage', label: `Excess baggage × ${bagCount}`, value: inr(bg), show: bg > 0 },
          { key: 'taxes', label: 'Taxes and Fees', value: '₹ 0.00' },
        ]}
        total={{ label: 'Total Amount Payable', value: inr(grand) }}
      />
      <p className="text-[10px] text-gray-400 mt-5 border-t border-gray-200 pt-3">This is a computer-generated e-voucher and does not require a signature. Carry a government photo ID and this voucher at the airport. Web check-in opens 48 hours before departure.</p>
    </>
  );
}

function HotelVoucherDoc({ hotel, room, rate, nights, guest, userEmail }) {
  const total = (rate && rate.price) || (room?.rates && room.rates[0]?.price) || 0;
  const ref = 'NH' + String(total).slice(0, 6);
  return (
    <>
      <VoucherHeader />
      <div className="mt-4">
        <p className="text-[#10b981] font-bold">Confirmed.</p>
        <p className="text-xs text-gray-500">Booking ID: <span className="font-bold text-[#0A0F1E]">{ref}</span></p>
      </div>
      <h3 className="font-bold text-base mt-4">{hotel?.name} {hotel?.stars ? <span className="text-[#f59e0b]">{'★'.repeat(hotel.stars)}</span> : null}</h3>
      <p className="text-xs text-gray-500">{[hotel?.address?.city, hotel?.address?.state, hotel?.address?.country].filter(Boolean).join(', ') || hotel?.area || ''}</p>
      <div className="grid grid-cols-4 gap-px mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-200 text-center">
        <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Check In</p><p className="font-semibold">Fri, 12 Jun</p><p className="font-semibold">2:00 PM</p></div>
        <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Check Out</p><p className="font-semibold">Sat, 13 Jun</p><p className="font-semibold">12:00 PM</p></div>
        <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Guests</p><p className="font-semibold">2 Adults</p><p className="font-semibold">1 Room</p></div>
        <div className="p-3 bg-gray-50"><p className="text-[10px] text-gray-500">Stay</p><p className="font-semibold">{nights || 1} Night</p></div>
      </div>
      <div className="mt-4">
        <p className="text-[11px] text-gray-500">Room</p>
        <p className="font-semibold">{room?.name}</p>
        <p className="text-[11px] text-gray-500 mt-2">Guest</p>
        <p className="font-semibold">{guest?.fullName || [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') || '—'}</p>
        <p className="text-[11px] text-gray-500 mt-2">Contact</p>
        <p className="font-semibold">{[guest?.email || userEmail, guest?.phone].filter(Boolean).join(' · ') || '—'}</p>
      </div>
      <h4 className="font-bold mt-5 text-[#0A0F1E]">Price Summary</h4>
      <div className="mt-1 text-sm">
        <PriceRow k="Base Fare" v={inr(total)} />
        <PriceRow k="Taxes and Fees" v="₹ 0.00" />
        <PriceRow k="Total Amount Payable" v={inr(total)} bold />
      </div>
      <p className="text-[10px] text-gray-400 mt-5 border-t border-gray-200 pt-3">This is a computer-generated e-voucher. Guest photo ID must be presented at check-in. City tax / resort fee (if any) to be paid directly to the hotel.</p>
    </>
  );
}

export default function VoucherModal({ open, onClose }) {
  const { hotel, room, rate, flight, fare, selectedFare, legs, guest, userEmail, mode, seat, seatPrice, seatType, baggageSel, paxName, nights } = useBooking((s) => ({
    hotel: s.hotel, room: s.room, rate: s.rate,
    flight: s.flight, fare: s.fare, selectedFare: s.selectedFare,
    legs: s.selectedLegs, guest: s.guest, userEmail: s.userEmail, mode: s.mode,
    seat: s.seat, seatPrice: s.seatPrice, seatType: s.seatType,
    baggageSel: s.baggageSel, paxName: s.paxName, nights: s.nights,
  }));

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="print-unclip fixed inset-0 z-[88] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="print-unclip relative w-full max-w-md max-h-[90svh] overflow-y-auto no-scrollbar rounded-3xl border border-line bg-panel p-6">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>
        <h2 className="font-display text-xl font-extrabold mb-4">Booking voucher</h2>
        <div>
          <div id="voucherDoc" className="bg-white text-[#1a1a2e] rounded-xl p-6 text-sm">
            {mode === 'hotel'
              ? <HotelVoucherDoc hotel={hotel} room={room} rate={rate} nights={nights} guest={guest} userEmail={userEmail} />
              : <FlightVoucherDoc
                  legs={legs}
                  flight={flight}
                  fare={fare}
                  selectedFare={selectedFare}
                  guest={guest}
                  userEmail={userEmail}
                  seat={seat}
                  seatPrice={seatPrice}
                  seatType={seatType}
                  baggageSel={baggageSel}
                  paxName={paxName}
                />}
          </div>
        </div>
        <button onClick={() => window.print()} className="w-full mt-4 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M19 8h-1V3H6v5H5a3 3 0 0 0-3 3v6h4v3h12v-3h4v-6a3 3 0 0 0-3-3M8 5h8v3H8zm8 14H8v-4h8z" /></svg>
          Download / print voucher
        </button>
      </div>
    </div>
  );
}
